import { useMemo, useState } from "react";
import { comics } from "@/mocks/comics";

const allCategories = ["全部", ...Array.from(new Set(comics.map((c) => c.category)))];

export default function LatestUpdates() {
  const [keyword, setKeyword] = useState("");
  const [activeCat, setActiveCat] = useState("全部");
  const [favs, setFavs] = useState<number[]>([]);

  const filtered = useMemo(() => {
    let list = comics;
    if (activeCat !== "全部") list = list.filter((c) => c.category === activeCat);
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return list;
  }, [activeCat, keyword]);

  const toggleFav = (id: number) =>
    setFavs((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <section id="updates" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-[1280px] px-4 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-600">
              <i className="ri-flashlight-fill"></i> 实时同步
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-4xl">
              最新更新
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600 md:text-base">
              今天上线的连载与最新话，按更新时间排序，收藏后每次打开都能看到有没有新章节。
            </p>
          </div>

          <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5 md:w-72">
            <i className="ri-search-line text-base text-foreground-500"></i>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜作品 / 作者 / 标签"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {allCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActiveCat(cat)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                activeCat === cat
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-12 flex flex-col items-center rounded-lg border border-background-200 bg-background-100/60 px-6 py-16 text-center">
            <i className="ri-inbox-line text-4xl text-foreground-400"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到相关作品，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((c) => {
              const isFav = favs.includes(c.id);
              return (
                <article
                  key={c.id}
                  className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={c.cover}
                      alt={`${c.title} ${c.category}漫画封面`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-3 top-3 rounded-md bg-black/55 px-2.5 py-1 text-xs font-medium text-background-50 backdrop-blur-sm">
                      {c.status}
                    </span>
                    <button
                      type="button"
                      onClick={() => toggleFav(c.id)}
                      aria-label={isFav ? "取消收藏" : "收藏追更"}
                      className={`absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
                        isFav
                          ? "bg-accent-500 text-background-50"
                          : "bg-black/40 text-background-50 hover:bg-black/60"
                      }`}
                    >
                      <i className={`${isFav ? "ri-heart-3-fill" : "ri-heart-3-line"} text-lg`}></i>
                    </button>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-bold text-foreground-950">{c.title}</h3>
                    <p className="mt-0.5 text-xs text-foreground-500">{c.author}</p>
                    <p className="mt-3 line-clamp-2 text-xs leading-relaxed text-foreground-600">
                      {c.intro}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] text-secondary-900"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                      <div>
                        <p className="text-sm font-semibold text-primary-600">{c.latestChapter}</p>
                        <p className="mt-0.5 text-[11px] text-foreground-400">
                          {c.chapters} 话 · {c.updateTime}
                        </p>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-fire-line text-accent-500"></i>
                        {(c.hot / 1000).toFixed(1)}k
                      </span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}